"use client";

import {useCallback, useEffect, useMemo, useState} from "react";

import { PANEL_WIDTH_PX } from "@/components/learn-piece/shell/panel-metrics";
import {
  recordHarmonyPhase,
  recordHarmonyUnitChained,
  useHarmonyAnalytics,
  type HarmonyPhase,
} from "@/hooks/use-harmony-analytics";
import { useNoteLabel, useNoteNotation } from "@/hooks/use-notation";
import { useTransportSeek } from "@/hooks/harmony/use-transport-seek";
import { buildFrieze, EMPTY_FRIEZE, type Frieze } from "@/lib/harmony/frieze";
import { keySignatureOf } from "@/lib/harmony/keysignature";
import { spellingOf } from "@/components/learn-piece/phrase-chords";
import { chordPitchClasses } from "@/lib/music/theory-helpers";
import { chordShortLabel } from "@/components/learn-piece/s0-copy";
import { chordPosition } from "@/lib/harmony/position";
import { relationOf } from "@/lib/harmony/key-spans";
import type { FriezeKey } from "@/lib/harmony/frieze";
import type { NoteNotation } from "@/lib/music/notation";
import type { HarmonyContext } from "@/lib/harmony/hud";
import { buildPhrasesFromSong, buildSongSpan, type SongLike } from "@/lib/learning-journey/phrase-plan";
import type { Phrase, SongSpan } from "@/lib/learning-journey/types";
import { rungWords } from "@/lib/levels/words";
import type { Song } from "@/lib/music/song";
import { tonalityFromDetectedKey, type Tonality, tonalityLabel } from "@/lib/music/tonality";
import { HarmonyScene } from "./harmony-scene";
import { SasScreen } from "./sas-screen";
import { TICK_EPSILON, withoutWrittenBass } from "@/lib/harmony/cursor";
import { isSasRung, type SasWindow } from "@/lib/harmony/sas";
import { playableChords, unitsWithSomethingToPlay } from "./playable";

/**
 * Les sas déjà lus, pour cette session, par morceau et par niveau.
 *
 * Le mécanisme est celui de `useSeam` : un ensemble en mémoire, hors React
 * et hors stockage. C'est un **moment** et pas un état du joueur. Un
 * rechargement ou une reprise le lendemain rendent la version longue, ce qui
 * est souhaitable — quelqu'un qui revient trois semaines plus tard a intérêt à
 * relire ce qu'il a lu une fois — et ne demande aucun magasin que cet écran
 * n'a pas.
 *
 * La clé est le morceau **et** le niveau : deux morceaux n'ont pas le même
 * inventaire, et deux niveaux n'ont pas le même bloc central. Le morceau y est
 * désigné par son titre et son compositeur, faute d'identifiant sur `Song` ;
 * pour un ensemble qui ne survit pas à un rechargement, deux morceaux
 * homonymes ouverts dans la même session sont un prix acceptable.
 */
const readSas = new Set<string>();

/** Les prises d'un morceau qui n'est pas encore là. */
const NO_GRIPS: SongSpan["gripsByHand"] = { left: [], right: [] };

/**
 * Le parcours harmonique, tenu sur un niveau.
 *
 * C'est la porte d'entrée de l'écran de jeu : l'échelle pose les notes d'un
 * niveau sous les mains et le déclare au studio, cet objet-ci les fait
 * travailler d'un bout à l'autre. Il est à `HarmonyScene` ce que
 * `learn-piece-page` est aux écrans du parcours par cœur, en beaucoup plus
 * petit, et les deux restent séparés : celui-là tient une machine à états,
 * celui-ci tient une seule règle.
 *
 * ## Un écran avant le premier temps
 *
 * Sur les niveaux qui en ont un, le sas prépare ce qu'on va jouer. Le décompte
 * l'attend : quatre temps qui s'écouleraient derrière un écran qu'on lit
 * arriveraient au moment précis où l'on n'a encore rien posé. L'entrée du
 * programme, elle, vit en amont et hors de cet écran : c'est elle qui a choisi
 * ce niveau, et elle est un état de la fiche du morceau. Voir
 * `PieceOverviewOverlay`.
 *
 * ## La seule règle, pour l'instant
 *
 * Le niveau se traverse d'un trait, et quand son dernier accord a sonné il
 * est fini et l'écran le dit. Il n'y a pas d'autre pas ici : reconnaître,
 * former et traverser ne sont pas encore écrits, et un pas qu'on n'a pas
 * encore décidé vaut mieux absent qu'esquissé.
 *
 * Il s'est traversé unité par unité, sur le plan de phrases du morceau, et
 * la scène était remontée à chacune. Le découpage n'avait rien à montrer ni
 * rien à décider, et il coupait la musique à chaque frontière. Il reste ce
 * qu'il est vraiment ici : le grain sur lequel la session mesure ce qui a été
 * atteint et enchaîné, en unités comme l'autre parcours.
 *
 * Rien n'est chronométré, rien n'est compté et rien n'est jugé. La scène n'a ni
 * transport, ni décompte, ni tempo : lui donner un régime en rythme
 * demanderait de lui donner d'abord tout cela.
 *
 * ## Les unités se lisent sur le morceau chargé, pas sur l'échelle
 *
 * Le morceau chargé **est** le niveau, puisque « Travailler ce niveau » a
 * remplacé les notes par les siennes. Le découper ici, exactement comme le fait
 * `use-learning-journey` pour le parcours par cœur, garde une seule réponse à
 * « quelles unités ». Redemander ce découpage à l'échelle en ferait deux, qui
 * divergeraient le jour où l'une des deux se tromperait de morceau.
 *
 * Les notes et les prises que la scène reçoit, elles, ne passent plus par le
 * découpage. Elles en venaient, unité après unité mise bout à bout, et une
 * unité ne porte que les notes qui commencent chez elle : un accord tenu
 * par-dessus une frontière se coupait en deux, et sa moitié sans note
 * disparaissait de la frise. Elles se lisent donc sur le morceau d'un seul
 * tenant. Voir `buildSongSpan`.
 */
export function HarmonyLevel({
  rung,
  song,
  pieceId,
}: {
  /**
   * Le niveau posé sous les mains.
   *
   * Jamais `null` : il voyage **dans** l'engagement du studio, donc le parcours
   * harmonique ne peut pas être en cours sans lui. Il l'a été, du temps où cet
   * écran était une vue avec sa route : `/studio/level` rechargé rendait la vue
   * sans le niveau, et l'écran affichait zéro unité sous un titre de morceau.
   * Voir `Engagement`.
   */
  readonly rung: HarmonyContext;
  /** Le morceau chargé, c'est-à-dire les notes du niveau. */
  readonly song: Song | null;
  /**
   * L'identité **catalogue** du morceau, pour la mesure et pour elle seule.
   *
   * `null` quand le morceau ne vient pas du catalogue. Elle descend jusqu'ici
   * parce que les sessions du parcours harmonique n'en portaient aucune : le
   * champ `journey` promettait de comparer les deux parcours, et la
   * comparaison s'arrêtait au premier « sur quel morceau ».
   */
  readonly pieceId: string | null;
}) {

  /**
   * Les unités que ce niveau fait travailler, pour la mesure et pour elle seule.
   *
   * Le découpage vient du morceau ; le filtre vient du niveau. Une unité dont
   * aucune fenêtre d'accord ne reçoit de note est une unité que ce niveau ne
   * dote pas : la compter ferait attendre une fin qui ne peut pas venir. Voir
   * `unitsWithSomethingToPlay`.
   */
  const units = useMemo<readonly Phrase[]>(
    () =>
      song === null
        ? []
        : unitsWithSomethingToPlay(rung.chords, buildPhrasesFromSong(asSongLike(song))),
    [song, rung],
  );

  /** Le morceau chargé d'un seul tenant : toutes les notes du niveau, et ses prises. */
  const span = useMemo<SongSpan | null>(
    () => (song === null ? null : buildSongSpan(asSongLike(song))),
    [song],
  );

  /** Ce que la scène demande du morceau, et rien de plus. */
  const timing = useMemo(() => {
    if (song === null) {
      return null;
    }

    const timeline = song.getMusicalTimeline();
    const ppq = timeline.getPpq();
    // Le temps vaut une noire en 4/4 mais une croche en 6/8 : c'est le
    // dénominateur qui donne l'unité, et la barre graduée sous les notes la
    // suit. Lu au tick 0, comme le tempo juste au-dessus, parce qu'une fenêtre
    // d'accord ne change pas de chiffrage en son milieu.
    const signature = timeline.getTimeSignatureAtTick(0);

    return {
      bpm: timeline.getBpmAtTick(0),
      ppq,
      beatTicks: (ppq * 4) / signature.denominator,
      beatsPerBar: signature.numerator,
    };
  }, [song]);

  /**
   * Le ruban du rail : tout ce que ce niveau donne à jouer, bout à bout.
   *
   * Construit ici et pas dans la scène, parce que c'est le seul endroit où il
   * se mémorise sur le niveau, et que le prix — un découpage de rythme par
   * fenêtre du niveau — est payé une fois par ouverture, jamais pendant qu'on
   * joue.
   */
  const notation = useNoteNotation();
  const spellNote = useNoteLabel();

  /**
   * Ce que ce niveau donne à jouer, fenêtre d'accord par fenêtre, dans l'ordre.
   *
   * Trois objets le lisent et doivent lire le même : la scène, qui le fait
   * traverser ; le rail, qui le dessine pendant qu'on joue ; le sas, qui en
   * montre le début avant qu'on commence. Le découper deux fois donnerait deux
   * réponses à « qu'est-ce qu'on joue », dont l'une finirait par se tromper de
   * niveau.
   */
  const played = useMemo(
    () => (span === null ? [] : playableChords(rung.chords, span)),
    [rung.chords, span],
  );

  /**
   * Les prises du niveau, main par main, entières.
   *
   * Le doigté d'une fenêtre se lit sur la première prise qui la chevauche.
   * Elles se mettaient bout à bout depuis les unités, coupées à chaque
   * frontière ; le doigté n'en changeait pas, une prise étant une durée où
   * aucun doigt ne change de touche, mais rien ne justifie plus la coupe.
   */
  const grips = span?.gripsByHand ?? NO_GRIPS;

  /**
   * La tonalité du morceau, que deux écrans lisent pour deux raisons.
   *
   * Le sas la nomme en toutes lettres, parce que c'est l'écran dont le métier
   * est de poser le cadre : « la place de l'accord dans Si mineur » ne s'écrit
   * pas sans la tonique.
   *
   * La frise n'en garde que la tonique, et pas pour l'écrire : pour **épeler**
   * ce que l'accord n'énonce pas. Une note nommée par l'accord suit l'accord et
   * change donc de lettre quand il change ; nommée par la tonalité, elle garde
   * la sienne d'un bout à l'autre du niveau, et reste sur sa ligne. Voir
   * `windowToneName`.
   *
   * Calculée ici, au-dessus des deux, parce qu'elle est une propriété du
   * morceau et non de l'écran qui la lit.
   */
  const tonality = useMemo<Tonality | null>(
    () => tonalityFromDetectedKey(song?.getKey() ?? null),
    [song],
  );

  const frieze = useMemo<Frieze>(() => {
    if (timing === null) {
      return EMPTY_FRIEZE;
    }

    return buildFrieze({
      windows: played.map((entry) => ({
        startTick: entry.startTick,
        endTick: entry.endTick,
        left: entry.left.notes,
        right: entry.right.notes,
        rootSpelling: entry.chord.root,
        rootPitchClass: entry.chord.rootPitchClass,
        // Ce que l'accord énonce, lu sur sa qualité : c'est lui qui décide
        // quelles notes il a le droit d'orthographier, et lesquelles reviennent
        // à la tonalité. Voir `windowToneName`.
        chordPitchClasses: chordPitchClasses(entry.chord.rootPitchClass, entry.chord.quality),
        spelling: spellingOf(entry),
        chordName: chordShortLabel({ chord: entry.chord }, notation),
        // La tonalité depuis laquelle l'analyseur a compté ce degré, quand le
        // couplet est lu sur la relative ; nommée ici, dans la notation du
        // joueur, comme le nom de l'accord.
        key: friezeKeyOf(entry.chord.localKey ?? null, tonality, notation),
        // Nommée aussi sur l'état fondamental, pour la raison qui vaut sur la
        // pastille : un objet muet se lirait « on ne sait pas » là où le
        // silence d'une ligne de texte se lisait « rien à signaler ».
        inversion: chordPosition(entry.chord.rootPitchClass, [
          ...entry.right.midis,
          ...entry.right.sustainedMidis,
        ]),
      })),
      beatTicks: timing.beatTicks,
      beatsPerBar: timing.beatsPerBar,
      spellNote,
      keyTonic: tonality?.tonic ?? null,
      signature: keySignatureOf(tonality),
      homeKeyName: tonality ? tonalityLabel(tonality, notation) : null,
    });
  }, [played, timing, notation, spellNote, tonality]);

  /**
   * Les mêmes fenêtres, réduites à ce que le sas en montre.
   *
   * Frappé **et** tenu des deux côtés : c'est la pile entière qui donne la
   * position d'un accord, et c'est elle que le rail lit trois lignes plus haut.
   */
  const sasWindows = useMemo<readonly SasWindow[]>(
    () =>
      played.map((entry) => ({
        symbol: withoutWrittenBass(entry.chord.symbol),
        root: entry.chord.root,
        rootPitchClass: entry.chord.rootPitchClass,
        chordPitchClasses: chordPitchClasses(entry.chord.rootPitchClass, entry.chord.quality),
        spelling: spellingOf(entry),
        left: [...entry.left.midis, ...entry.left.sustainedMidis],
        right: [...entry.right.midis, ...entry.right.sustainedMidis],
      })),
    [played],
  );

  /**
   * Les ticks en secondes, par la carte de tempo du morceau.
   *
   * L'écoute du passage d'origine en a besoin, et un tempo moyen ne suffirait
   * pas : un ritardando écrit décalerait l'extrait de plus en plus au fil du
   * morceau. La carte est la même des deux côtés, le niveau ne change que les
   * notes.
   */
  const tickToSeconds = useMemo(() => {
    if (song === null) {
      return null;
    }

    const timeline = song.getMusicalTimeline();

    return (tick: number) => timeline.tickToSeconds(tick);
  }, [song]);

  /**
   * Le sas de ce niveau est-il derrière nous.
   *
   * Remis à zéro au rendu quand le niveau change d'objet, comme l'unité juste
   * en dessous et pour la même raison : « Travailler ce niveau » redonne un
   * objet neuf à chaque clic, donc rouvrir deux fois le même niveau remontre
   * deux fois son sas. C'est voulu — le décompte part quand même, et un écran
   * qui disparaît un jour sur deux se lit comme un bug — et c'est la version
   * courte qui absorbe la répétition.
   */
  const [gate, setGate] = useState<{
    readonly rung: HarmonyContext;
    readonly entered: boolean;
  }>(() => ({ rung, entered: false }));

  if (gate.rung !== rung) {
    setGate({ rung, entered: false });
  }

  /** La clé de session du sas, ou `null` tant qu'il n'y a rien à ouvrir. */
  const sasKey = useMemo<string | null>(() => {
    if (song === null) {
      return null;
    }

    return `${song.getTitle() ?? ""} · ${song.getComposer() ?? ""}:${rung.rungId}`;
  }, [rung, song]);

  /**
   * Le sas est passé, ou ce niveau n'en a pas.
   *
   * C'est ce qui autorise le décompte à partir : sans cette garde, les quatre
   * temps s'écouleraient derrière l'écran pendant qu'on le lit.
   */
  const opened = !isSasRung(rung.rungIndex) || (gate.rung === rung && gate.entered);

  const enterLevel = useCallback(() => {
    if (sasKey !== null) {
      readSas.add(sasKey);
    }

    setGate((current) => ({ rung: current.rung, entered: true }));
  }, [sasKey]);

  /**
   * Le niveau a été enchaîné jusqu'à son dernier accord.
   *
   * Tenu avec le plan auquel il appartient, parce que c'est lui qui le
   * périme : changer de niveau change les notes, donc le découpage, et un
   * niveau fini n'est pas le suivant fini. Remis à zéro au rendu plutôt que
   * dans un effet, pour que la première image d'un niveau soit son écran de
   * jeu et non la fin du précédent.
   */
  const [chained, setChained] = useState<{
    readonly plan: readonly Phrase[];
    readonly done: boolean;
  }>(() => ({ plan: units, done: false }));

  if (chained.plan !== units) {
    setChained({ plan: units, done: false });
  }

  const done = chained.plan === units && chained.done;

  /** Jusqu'où va le niveau : la fin de sa dernière fenêtre. */
  const reach = played.length > 0 ? played[played.length - 1].endTick : 0;

  /**
   * La tête de lecture bouge, le niveau se rouvre.
   *
   * Un niveau terminé se remet à jouer dès qu'on désigne une mesure dans la
   * minimap ou une section dans la structure : c'est le roll qui dit où l'on
   * en est, et l'écran le suit. La fin du dernier segment, elle, dépose la
   * tête exactement au bout du niveau, et ce dépôt-là n'est pas une
   * désignation : en deçà seulement.
   *
   * L'abonnement rend aussi la position au montage, ce qui n'est pas un saut
   * mais compte autant : un écran qui s'ouvre se pose là où le morceau en est.
   */
  const followSeek = useCallback(
    (tick: number) => {
      if (tick < reach - TICK_EPSILON) {
        setChained((current) => (current.done ? { plan: current.plan, done: false } : current));
      }
    },
    [reach],
  );

  useTransportSeek(followSeek);

  /**
   * Le dernier accord du niveau a fini de sonner : le niveau est fini, et
   * l'écran le dit.
   */
  const handleChained = useCallback(() => {
    setChained((current) => ({ plan: current.plan, done: true }));
  }, []);

  /** Une unité vient d'être enchaînée : la session la compte. */
  const handleUnitChained = useCallback(
    (unitId: string) => {
      recordHarmonyUnitChained(rung.rungId, unitId);
    },
    [rung.rungId],
  );

  /**
   * La session de pratique du niveau, du montage de cet écran à sa sortie.
   *
   * Elle compte en unités, comme celle de l'autre parcours : l'écran ne les
   * montre plus, mais elles restent le grain de « jusqu'où » qui rend les deux
   * parcours comparables.
   */
  useHarmonyAnalytics({ rungId: rung.rungId, unitCount: units.length, pieceId });

  /**
   * L'écran que ce rendu va rendre, quand c'est ce composant qui le rend.
   *
   * `null` quand la scène prend la main, parce qu'elle seule sait si la couture
   * passe devant le niveau : deux réponses à « quel écran » finiraient par se
   * contredire, et c'est la couture qui disparaîtrait.
   */
  const screen = useMemo<HarmonyPhase | null>(() => {
    if (song === null || timing === null || tickToSeconds === null) {
      return null;
    }

    if (played.length === 0) {
      return "nothing_to_play";
    }

    if (!opened) {
      return "sas";
    }

    return done ? "level_done" : null;
  }, [song, timing, tickToSeconds, played, opened, done]);

  useEffect(() => {
    if (screen !== null) {
      recordHarmonyPhase(rung.rungId, screen);
    }
  }, [screen, rung.rungId]);

  // Le niveau est garanti, le morceau non : le studio peut être entre deux
  // chargements. C'est la seule raison qui reste de ne rien rendre.
  if (song === null || timing === null || tickToSeconds === null) {
    return (
      <Notice
        title="Aucun morceau sous les mains"
        body="Ouvre un morceau, puis choisis un niveau depuis sa fiche."
      />
    );
  }

  const words = rungWords(rung.rungId);

  if (played.length === 0) {
    return (
      <Notice
        eyebrow={words.label}
        title="Rien à travailler ici"
        body="Ce niveau ne met aucune note sous les mains dans ce morceau."
      />
    );
  }

  if (done) {
    return (
      <Notice
        eyebrow={words.label}
        title="Niveau terminé"
        body="Tous les accords de ce niveau ont été enchaînés."
      />
    );
  }

  /*
    Le sas prend l'écran entier, comme la couture le fait
    depuis la scène : tant qu'il est là on ne joue pas, et rien ne peut avancer
    derrière lui puisque la scène n'est pas montée. Il est ici et non dans
    `HarmonyScene` parce que c'est ici que part le décompte, et que c'est
    exactement ce qu'il doit retenir.
  */
  if (!opened) {
    return (
      <Overlay>
        <SasScreen
          harmony={rung}
          title={song.getTitle()}
          composer={song.getComposer()}
          tonality={tonality}
          windows={sasWindows}
          chords={played.length}
          short={sasKey !== null && readSas.has(sasKey)}
          onStart={enterLevel}
        />
      </Overlay>
    );
  }

  return (
    <HarmonyScene
      harmony={rung}
      entries={played}
      grips={grips}
      units={units}
      frieze={frieze}
      bpm={timing.bpm}
      ppq={timing.ppq}
      beatTicks={timing.beatTicks}
      beatsPerBar={timing.beatsPerBar}
      tickToSeconds={tickToSeconds}
      onChained={handleChained}
      onUnitChained={handleUnitChained}
    />
  );
}

/**
 * Le `Song` du runtime, lu par les sept questions du plan de phrases.
 *
 * Le frottement est sur `AnnotatedNote.note` : le plan le type structurellement
 * `{ readonly midi: number }`, le vrai `Note` garde `midi` privé et l'expose par
 * `toMidi()`. `private` n'existe qu'à la compilation, donc le champ est bien là
 * à l'exécution et la conversion est saine. `learn-piece-page` tient la même
 * pour le parcours par cœur ; la partager demanderait de faire descendre `Song`
 * dans `phrase-plan`, c'est-à-dire de lui retirer exactement l'indépendance qui
 * lui fait décrire un `SongLike` structurel.
 */
function asSongLike(song: Song): SongLike {
  return song as unknown as SongLike;
}

/**
 * Le sas prend l'écran, sur le fond de la scène.
 *
 * Tout l'écran, clavier du roll compris, comme le `Notice` juste en dessous :
 * la vue de niveau descend jusqu'en bas du studio, et le sas est une carte
 * qu'on lit avant de jouer. Rien ne tombe derrière lui.
 */
function Overlay({ children }: { readonly children: React.ReactNode }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 30,
        background: "var(--color-bg-deep)",
        pointerEvents: "auto",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Ce que l'écran dit quand il n'y a rien à jouer.
 *
 * Opaque et sur toute la hauteur, comme la scène elle-même : il
 * n'y a rien à jouer, donc rien à garder sous les mains.
 */
function Notice({
  eyebrow,
  title,
  body,
}: {
  readonly eyebrow?: string;
  readonly title: string;
  readonly body: string;
}) {
  return (
    <div
      role="status"
      className="flex h-full items-center justify-center"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 30,
        background: "var(--color-bg-deep)",
        pointerEvents: "auto",
        padding: 24,
      }}
    >
      <div style={{ width: PANEL_WIDTH_PX, maxWidth: "100%", textAlign: "center" }}>
        {eyebrow !== undefined && (
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--color-text-muted)",
            }}
          >
            {eyebrow}
          </div>
        )}

        <h2
          style={{
            margin: eyebrow === undefined ? 0 : "8px 0 0",
            fontSize: 20,
            color: "var(--color-text-primary)",
          }}
        >
          {title}
        </h2>

        <p
          style={{
            margin: "12px 0 0",
            fontSize: 14,
            lineHeight: 1.6,
            color: "var(--color-text-soft)",
          }}
        >
          {body}
        </p>
      </div>
    </div>
  );
}

/**
 * La tonalité locale d'un accord, écrite pour la frise.
 *
 * `null` quand l'accord est compté depuis la tonalité du morceau, ou quand on
 * ne connaît pas celle-ci : sans elle, on ne saurait pas dire ce que l'autre
 * lui est, et « Ré mineur » sans « relatif » n'enseigne rien.
 */
function friezeKeyOf(
  localKey: { tonic: string; mode: "major" | "minor" } | null,
  tonality: Tonality | null,
  notation: NoteNotation,
): FriezeKey | null {
  if (localKey === null || tonality === null) {
    return null;
  }

  const here: Tonality = { tonic: localKey.tonic, mode: localKey.mode };

  return { name: tonalityLabel(here, notation), relation: relationOf(here, tonality) };
}
